'use client'

import { Check, Copy } from 'lucide-react'
import posthog from 'posthog-js'
import { useEffect, useRef, useState, type ComponentPropsWithoutRef } from 'react'

type CopyableCodeBlockProps = ComponentPropsWithoutRef<'pre'> & { node?: unknown }

/**
 * A markdown code block with a copy button, for posts that hand the reader a
 * string to paste somewhere else (e.g. a term to Ctrl+F in a linked PDF).
 * Plugged into MarkdownRenderer as its `pre` component when `copyableCode` is set.
 */
export function CopyableCodeBlock({ node: _node, className, ...props }: CopyableCodeBlockProps) {
  const preRef = useRef<HTMLPreElement>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(timer)
  }, [copied])

  const handleCopy = async () => {
    const text = (preRef.current?.textContent ?? '').replace(/\n$/, '')
    await navigator.clipboard.writeText(text)
    setCopied(true)
    posthog.capture('evidence_code_copied', { path: window.location.pathname, text })
  }

  return (
    <div className="relative">
      <pre ref={preRef} className={`pr-12 ${className ?? ''}`} {...props} />
      <button
        type="button"
        onClick={() => void handleCopy()}
        aria-label="Copy to clipboard"
        title="Copy to clipboard"
        className="absolute right-2 top-2 rounded p-1.5 text-gray-300 transition-colors hover:bg-white/10 hover:text-white"
      >
        {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  )
}
